import { API } from '@pibex/shared';
import type { MeResponse } from '@pibex/shared';
import { useCallback, useEffect, useRef, useState } from 'react';
import { useNavigate } from 'react-router';
import { Notice } from '../components/Notice.js';
import { es } from '../i18n/es.js';
import { api, isApiError } from '../lib/api.js';
import { loadGsiScript } from '../lib/gsi.js';
import { useAuth } from './AuthProvider.js';

type Status = 'idle' | 'pending';

export function GoogleSignInButton() {
  const clientId = import.meta.env.VITE_GOOGLE_CLIENT_ID;
  const { refresh } = useAuth();
  const navigate = useNavigate();
  const container = useRef<HTMLDivElement>(null);
  const [status, setStatus] = useState<Status>('idle');
  const [error, setError] = useState<string | null>(null);

  const handleCredential = useCallback(
    async (credential: string) => {
      setStatus('pending');
      setError(null);
      try {
        await api.post<MeResponse>(API.authGoogle, { credential });
        await refresh();
        navigate('/', { replace: true });
      } catch (err) {
        if (isApiError(err, 'not_invited')) {
          navigate('/not-invited', { replace: true });
          return;
        }
        if (isApiError(err, 'disabled')) {
          navigate('/not-invited?reason=disabled', { replace: true });
          return;
        }
        setStatus('idle');
        if (isApiError(err, 'invalid_token')) setError(es.login.invalidToken);
        else if (isApiError(err)) setError(es.genericError);
        else setError(es.networkError);
      }
    },
    [navigate, refresh],
  );

  // Keep the latest handler reachable from the GIS callback without re-initializing GIS.
  const latestHandler = useRef(handleCredential);
  useEffect(() => {
    latestHandler.current = handleCredential;
  }, [handleCredential]);

  useEffect(() => {
    if (!clientId) return;
    let cancelled = false;
    loadGsiScript()
      .then(() => {
        const el = container.current;
        const gsi = window.google?.accounts.id;
        if (cancelled || !el || !gsi) return;
        gsi.initialize({
          client_id: clientId,
          callback: (res) => void latestHandler.current(res.credential),
        });
        const width = Math.min(Math.max(el.clientWidth || 320, 200), 400);
        gsi.renderButton(el, {
          theme: 'outline',
          size: 'large',
          width,
          text: 'continue_with',
          shape: 'rectangular',
          locale: 'es',
        });
      })
      .catch(() => {
        if (!cancelled) setError(es.login.scriptError);
      });
    return () => {
      cancelled = true;
    };
  }, [clientId]);

  if (!clientId) {
    return <Notice tone="warning">{es.login.missingClientId}</Notice>;
  }

  return (
    <div className="flex flex-col items-stretch gap-3">
      <div
        ref={container}
        data-testid="google-signin"
        className="flex min-h-11 w-full justify-center"
        aria-busy={status === 'pending'}
      />
      <p role="status" aria-live="polite" className="min-h-5 text-center text-sm text-neutral-500">
        {status === 'pending' ? es.login.signingIn : null}
      </p>
      {error ? <Notice tone="error">{error}</Notice> : null}
    </div>
  );
}
